/**
 * Cancellation survey categories (GDN-2986, revised in GDN-3118).
 *
 * The paying-cancel dialog presents these behavioral-health-specific options as
 * a required multi-select. IDs are the contract with the server — they must
 * match the `CancelReasonCategory` enum in `smartscribe-server`
 * (control_plane/subscriptions provider schemas). The server maps the primary
 * (first-selected) category onto Stripe's native `feedback` enum and writes the
 * full selection as JSON into `cancellation_details.comment`.
 *
 * This is the single source of truth for the reason list — adding a reason is a
 * one-line change here plus a matching enum entry server-side (GDN-3118). IDs
 * are kept stable where a concept survives from GDN-2986 (`too_expensive`,
 * `switched_service`) so existing structured data stays comparable.
 *
 * Options are presented in randomized order per session (see
 * `getShuffledCancelReasons`) to kill primacy bias; "Other" always renders last.
 */

export const OTHER_CATEGORY_ID = 'other';

export const CANCEL_REASON_CATEGORIES = [
  { id: 'too_expensive', label: 'Too expensive for how much I use it' },
  {
    id: 'switched_service',
    label: 'I switched to another AI scribe (Freed, Heidi, Doximity, etc.)',
  },
  { id: 'ehr_scribe', label: 'My EHR has a scribe I can use (SimplePractice, Elation, etc.)' },
  { id: 'employer_scribe', label: 'My MSO or employer provides a scribe (Headway, Grow, etc.)' },
  { id: 'notes_fit', label: "The notes didn't fit how I write" },
  { id: 'reliability', label: 'Reliability problems (failed recordings, blank or incorrect notes)' },
  {
    id: 'situation_changed',
    label: 'My situation changed (new job, leave, closing my practice)',
  },
  { id: 'no_rhythm', label: 'I never got into a rhythm of using it' },
  { id: OTHER_CATEGORY_ID, label: 'Other' },
] as const;

export type CancelReasonCategory = (typeof CANCEL_REASON_CATEGORIES)[number];
export type CancelReasonCategoryId = CancelReasonCategory['id'];

/**
 * Returns the reason list with every option before "Other" shuffled via
 * Fisher–Yates and "Other" pinned last. Call once on dialog mount so the order
 * is stable for the session but varies across sessions — this removes the
 * position bias that let the first-listed option (previously "Too expensive")
 * lead the data by virtue of where it sat (GDN-3118).
 */
export function getShuffledCancelReasons(): CancelReasonCategory[] {
  const rest = CANCEL_REASON_CATEGORIES.filter(category => category.id !== OTHER_CATEGORY_ID);
  const other = CANCEL_REASON_CATEGORIES.filter(category => category.id === OTHER_CATEGORY_ID);

  // Fisher–Yates in place on the copy of the non-Other options.
  for (let i = rest.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }

  return [...rest, ...other];
}
